import { PROJECT_TYPE_LABELS, formatMoneyCad, type EstimatorInput, type EstimatorResult } from "@reno-estimator/core";
import type { EmailMessage } from "./types.js";

/**
 * What this email deliberately does NOT include: the four-part cost
 * breakdown, `explanation.steps` (per-line-item pricing detail, catalog
 * ids, applied multipliers), assumption/warning/exclusion *codes*, and
 * the pricing catalog version/date-of-record beyond the single
 * human-readable "pricing date" the brief asked for. All of that is
 * exactly the internal pricing methodology the brief says not to expose
 * - none of it is needed for a homeowner to understand their planning
 * numbers, and the same information is already visible (to the person
 * who generated the estimate) on the "how we calculated this" panel of
 * the results screen itself, which is a different, authenticated-by-
 * having-just-used-the-tool context than an email that could end up
 * forwarded anywhere.
 */

const SCOPE_LABELS: Record<EstimatorInput["scope"], string> = {
  cosmetic_refresh: "Cosmetic refresh",
  mid_range_remodel: "Standard renovation",
  full_gut_renovation: "Full gut renovation",
  custom_high_end: "Custom / high-end renovation"
};

/** Every assumption's `message` field is already written as end-user-safe plain language (see docs/calculation-assumptions.md) - only the internal `code` is withheld here, and only because it's a machine-readable id with no value to a homeowner. */
const MAX_ASSUMPTIONS_IN_EMAIL = 6;

function formatLocation(input: EstimatorInput): string {
  return input.location.city ? `${input.location.city}, ${input.location.province}` : input.location.province;
}

function formatDate(iso: string): string {
  try {
    // `result.pricingDate` is a plain calendar date ("2026-09-01"), not a timestamp - the engine
    // means "September 1", not any particular instant. `new Date(iso)` parses that as UTC midnight,
    // and without an explicit timeZone here, toLocaleDateString renders it in the *host machine's*
    // local timezone - so anywhere west of UTC (all of Canada included) it silently prints the day
    // before (e.g. "August 31, 2026" instead of "September 1, 2026"). Pinning timeZone: "UTC" reads
    // the same UTC-midnight instant back out as the calendar date it was always meant to represent,
    // so this renders identically no matter what timezone the email is generated in.
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
  } catch {
    return iso;
  }
}

function formatRange(range: EstimatorResult["estimate"]): string {
  return `${formatMoneyCad(range.low)} - ${formatMoneyCad(range.high)}`;
}

export interface EstimateEmailContentOptions {
  siteUrl: string;
}

/** Builds the plain-text and HTML bodies together, from the same data, so the two can never drift apart. */
export function buildEstimateEmailContent(input: EstimatorInput, result: EstimatorResult, options: EstimateEmailContentOptions): Pick<EmailMessage, "subject" | "text" | "html"> {
  const projectLabel = PROJECT_TYPE_LABELS[input.projectType];
  const location = formatLocation(input);
  const scopeLabel = SCOPE_LABELS[input.scope] ?? input.scope;
  const estimateRange = formatRange(result.estimate);
  const typical = formatMoneyCad(result.estimate.typical);
  const contingencyRange = formatRange(result.recommendedContingencyAmount);
  const planningRange = formatRange(result.planningBudget);
  const pricingDate = formatDate(result.pricingDate);
  const assumptions = result.assumptions.slice(0, MAX_ASSUMPTIONS_IN_EMAIL).map((a) => a.message);
  const siteUrl = options.siteUrl;

  const subject = `Your ${projectLabel} renovation estimate`;

  const disclaimer =
    "This is a planning-level estimate generated from the answers you provided - not a formal quote, bid, or contract. " +
    "Actual costs depend on a full in-person assessment, site conditions, material choices, and contractor pricing at the time of your project.";

  const notAQuoteLine = "Please note: this summary is for planning purposes only and does not constitute a formal quotation.";

  const textLines = [
    `Here's a summary of the ${projectLabel.toLowerCase()} estimate you just generated.`,
    "",
    `Project type: ${projectLabel}`,
    `Location: ${location}`,
    `Selected scope: ${scopeLabel}`,
    `Estimated construction range: ${estimateRange}`,
    `Typical estimate: ${typical}`,
    `Recommended contingency (${result.recommendedContingencyPercent}%): ${contingencyRange}`,
    `Suggested planning budget: ${planningRange}`,
    ""
  ];

  if (assumptions.length > 0) {
    textLines.push("Key assumptions:");
    for (const a of assumptions) textLines.push(`- ${a}`);
    textLines.push("");
  }

  textLines.push(`Pricing date: ${pricingDate}`, "", notAQuoteLine, "", disclaimer, "", `View or update your estimate: ${siteUrl}`);

  const text = textLines.join("\n");

  const escapeHtml = (value: string) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const assumptionsHtml =
    assumptions.length > 0
      ? `<h2 style="font-size:16px;margin:24px 0 8px;">Key assumptions</h2><ul style="margin:0 0 16px;padding-left:20px;">${assumptions
          .map((a) => `<li style="margin-bottom:4px;">${escapeHtml(a)}</li>`)
          .join("")}</ul>`
      : "";

  const html = `
<div style="font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;max-width:560px;margin:0 auto;">
  <h1 style="font-size:20px;margin-bottom:4px;">Your ${escapeHtml(projectLabel)} renovation estimate</h1>
  <p style="color:#555;margin-top:0;">${escapeHtml(notAQuoteLine)}</p>
  <table style="width:100%;border-collapse:collapse;margin:16px 0;">
    <tbody>
      <tr><td style="padding:4px 0;color:#555;">Project type</td><td style="padding:4px 0;text-align:right;font-weight:bold;">${escapeHtml(projectLabel)}</td></tr>
      <tr><td style="padding:4px 0;color:#555;">Location</td><td style="padding:4px 0;text-align:right;">${escapeHtml(location)}</td></tr>
      <tr><td style="padding:4px 0;color:#555;">Selected scope</td><td style="padding:4px 0;text-align:right;">${escapeHtml(scopeLabel)}</td></tr>
      <tr><td style="padding:4px 0;color:#555;">Estimated construction range</td><td style="padding:4px 0;text-align:right;font-weight:bold;">${escapeHtml(estimateRange)}</td></tr>
      <tr><td style="padding:4px 0;color:#555;">Typical estimate</td><td style="padding:4px 0;text-align:right;">${escapeHtml(typical)}</td></tr>
      <tr><td style="padding:4px 0;color:#555;">Recommended contingency (${result.recommendedContingencyPercent}%)</td><td style="padding:4px 0;text-align:right;">${escapeHtml(contingencyRange)}</td></tr>
      <tr><td style="padding:4px 0;color:#555;">Suggested planning budget</td><td style="padding:4px 0;text-align:right;font-weight:bold;">${escapeHtml(planningRange)}</td></tr>
    </tbody>
  </table>
  ${assumptionsHtml}
  <p style="color:#555;font-size:13px;">Pricing date: ${escapeHtml(pricingDate)}</p>
  <p style="margin:24px 0;"><a href="${escapeHtml(siteUrl)}" style="background:#1a5c3a;color:#fff;padding:10px 18px;border-radius:6px;text-decoration:none;">View or update your estimate</a></p>
  <p style="color:#888;font-size:12px;line-height:1.5;">${escapeHtml(disclaimer)}</p>
</div>`.trim();

  return { subject, text, html };
}
